<template>
    <v-card v-if="surchargeForEdit">
        <v-card-text>
            <v-form
                ref="form"
                v-model="isValidGeneral"
                :disabled="isSaveLoading"
                data-cy="product-prices-surcharge-form"
            >
                <v-row dense>
                    <v-col cols="12" md="6">
                        <v-text-field
                            v-model="surchargeForEdit.name"
                            :rules="[requiredRule('name')]"
                            density="compact"
                            hide-details
                            :label="`${$t('name')}*`"
                        />
                    </v-col>
                    <v-col cols="12" md="6">
                        <div class="full-width d-flex mt-1">
                            <v-spacer />
                            <v-switch
                                v-model="surchargeForEdit.active"
                                color="primary"
                                hide-details
                                :label="$t('active')"
                            />
                        </div>
                    </v-col>
                </v-row>

                <v-row dense>
                    <v-col cols="12">
                        <v-card variant="outlined" :loading="isTariffsLoading">
                            <v-card-title>
                                <span class="font-weight-bold text-subtitle-2">
                                    {{ $t('tariffs') }}
                                </span>
                            </v-card-title>
                            <v-card-text>
                                <v-data-table
                                    :items="surchargeForEdit.tariffLinks"
                                    :headers="headersTariffs"
                                    hide-default-footer
                                    :items-per-page="-1"
                                >
                                    <template v-slot:item.tariff="{item}">
                                        <div class="my-3">
                                            <v-select
                                                v-model="item.tariffId"
                                                :items="tariffItems(item.tariffId)"
                                                item-value="id"
                                                item-title="name"
                                                :rules="[requiredRule('tariff')]"
                                                hide-details
                                                density="compact"
                                                :disabled="item.tariffId && !tariffStatus(item.tariffId)"
                                            />
                                        </div>
                                    </template>
                                    <template v-slot:item.active="{item}">
                                        {{ tariffStatus(item.tariffId) ? $t('active') : $t('inactive') }}
                                    </template>
                                    <template v-slot:item.dates="{item}">
                                        <div class="d-flex align-center">
                                            <div class="me-4 full-width">
                                                <date-picker
                                                    v-model="item.dateFrom"
                                                    outlined
                                                    :density="'compact'"
                                                    :max="item.dateTo || '2100-01-01'"
                                                    :label="$t('date_from') + '*'"
                                                    :rules="[requiredRule('date_from')]"
                                                    hide-details
                                                />
                                            </div>
                                            <div class="full-width">
                                                <date-picker
                                                    v-model="item.dateTo"
                                                    outlined
                                                    :min="item.dateFrom || '1900-01-01'"
                                                    :density="'compact'"
                                                    :label="$t('date_to') + '*'"
                                                    :rules="[requiredRule('date_to')]"
                                                    hide-details
                                                />
                                            </div>
                                        </div>
                                    </template>
                                    <template v-slot:item.actions="{item, index}">
                                        <div class="full-width d-flex align-center">
                                            <v-spacer />
                                            <v-btn
                                                variant="text"
                                                size="small"
                                                color="primary"
                                                @click="copyTariffLink(item)"
                                            >
                                                {{ $t('copy') }}
                                            </v-btn>
                                            <v-btn
                                                size="small"
                                                color="error"
                                                variant="text"
                                                icon="mdi-close"
                                                @click="removeTariffLink(index)"
                                            />
                                        </div>
                                    </template>
                                </v-data-table>
                                <v-divider class="mb-4" />
                                <v-row dense>
                                    <v-col cols="12">
                                        <v-btn variant="flat" size="small" color="primary" @click="addTariffLink">
                                            {{ $t('add') }}
                                        </v-btn>
                                    </v-col>
                                </v-row>
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>

                <v-row v-if="isOwnHotel" dense>
                    <v-col cols="12">
                        <v-card variant="outlined" :loading="isMealTypesLoading">
                            <v-card-title>
                                <span class="font-weight-bold text-subtitle-2">
                                    {{ $t('extra_charge_for_board_type') }}
                                </span>
                            </v-card-title>
                            <v-card-text>
                                <v-data-table
                                    :items="mealTypes"
                                    :headers="headersMealTypes"
                                    hide-default-footer
                                    :items-per-page="-1"
                                >
                                    <template v-slot:item="{item}">
                                        <tr>
                                            <td class="text-no-wrap">{{ mealTypeName(item.id) }}</td>
                                            <td v-for="pax in paxes" :key="pax.id">
                                                <div class="d-flex align-center py-2" style="gap: 6px">
                                                    <v-number-input
                                                        v-if="getMealPrice(item.id, pax.id)"
                                                        v-model="getMealPrice(item.id, pax.id).amount"
                                                        :label="$t('amount')"
                                                        hide-details
                                                        density="compact"
                                                        :width="180"
                                                    />
                                                    <span v-if="tariffCurrency">{{ tariffCurrency }}</span>
                                                </div>
                                            </td>
                                        </tr>
                                    </template>
                                </v-data-table>
                                <div
                                    v-if="!mealTypes.length || !paxes.length"
                                    class="text-caption text-medium-emphasis pa-2"
                                >
                                    {{ $t('no_data') }}
                                </div>
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>

                <v-row v-if="isOwnHotel && extraPlaceRooms.length && paxes.length" dense>
                    <v-col cols="12">
                        <v-card variant="outlined">
                            <v-card-title>
                                <span class="font-weight-bold text-subtitle-2">
                                    {{ $t('extra_charge_for_extra_bed') }}
                                </span>
                            </v-card-title>
                            <v-card-text>
                                <v-data-table
                                    :items="extraPlaceRooms"
                                    :headers="headersExtraPlaces"
                                    hide-default-footer
                                    :items-per-page="-1"
                                >
                                    <template v-slot:item="{item}">
                                        <tr>
                                            <td class="text-no-wrap">{{ serviceTitle(item) }}</td>
                                            <td v-for="pax in paxes" :key="pax.id">
                                                <div class="d-flex align-center py-2" style="gap: 6px">
                                                    <v-number-input
                                                        v-if="getExtraPlacePrice(item.id, pax.id)"
                                                        v-model="getExtraPlacePrice(item.id, pax.id).amount"
                                                        :label="$t('amount')"
                                                        hide-details
                                                        density="compact"
                                                        :width="180"
                                                    />
                                                    <span v-if="tariffCurrency">{{ tariffCurrency }}</span>
                                                </div>
                                            </td>
                                        </tr>
                                    </template>
                                </v-data-table>
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>

                <v-row v-if="isOwnHotel && withoutPlaceRooms.length && paxes.length" dense>
                    <v-col cols="12">
                        <v-card variant="outlined">
                            <v-card-title>
                                <span class="font-weight-bold text-subtitle-2">
                                    {{ $t('extra_charge_for_sharing_place') }}
                                </span>
                            </v-card-title>
                            <v-card-text>
                                <v-data-table
                                    :items="withoutPlaceRooms"
                                    :headers="headersWithoutPlaces"
                                    hide-default-footer
                                    :items-per-page="-1"
                                >
                                    <template v-slot:item="{item}">
                                        <tr>
                                            <td class="text-no-wrap">{{ serviceTitle(item) }}</td>
                                            <td v-for="pax in paxes" :key="pax.id">
                                                <div class="d-flex align-center py-2" style="gap: 6px">
                                                    <v-number-input
                                                        v-if="getWithoutPlacePrice(item.id, pax.id)"
                                                        v-model="getWithoutPlacePrice(item.id, pax.id).amount"
                                                        :label="$t('amount')"
                                                        hide-details
                                                        density="compact"
                                                        :width="180"
                                                    />
                                                    <span v-if="tariffCurrency">{{ tariffCurrency }}</span>
                                                </div>
                                            </td>
                                        </tr>
                                    </template>
                                </v-data-table>
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>

                <!-- Accommodation Surcharges block hidden pending B2C application audit.
                <v-row dense>
                    <v-col cols="12">
                        <v-card variant="outlined" :loading="isServicesLoading">
                            <v-card-title>
                                <span class="font-weight-bold text-subtitle-2">
                                    {{ $t('accommodation_surcharges') }}
                                </span>
                            </v-card-title>
                            <v-card-text>
                                <v-data-table
                                    :items="accommodationVisible"
                                    :headers="headersAllocation"
                                    hide-default-footer
                                    :items-per-page="-1"
                                >
                                    <template v-slot:item.service="{item}">
                                        <div class="my-3">
                                            <v-select
                                                v-model="item.serviceId"
                                                :items="selectableServices"
                                                item-value="id"
                                                :item-title="serviceTitle"
                                                :rules="[requiredRule('service')]"
                                                hide-details
                                                density="compact"
                                            />
                                        </div>
                                    </template>
                                    <template v-slot:item.minGuests="{item}">
                                        <v-number-input
                                            v-model="item.minGuests"
                                            :min="0"
                                            hide-details
                                            density="compact"
                                        />
                                    </template>
                                    <template v-slot:item.maxGuests="{item}">
                                        <v-number-input
                                            v-model="item.maxGuests"
                                            :min="0"
                                            hide-details
                                            density="compact"
                                        />
                                    </template>
                                    <template v-slot:item.amount="{item}">
                                        <NumberInputWithButtons
                                            v-model="item.amount"
                                            :label="item.percentage ? `${$t('percent')}*` : `${$t('amount')}*`"
                                            :width="200"
                                            hide-details
                                            :rules="[requiredRule('')]"
                                            :buttons="[
                                                {
                                                    icon: 'mdi-percent',
                                                    active: item.percentage,
                                                    onClick: () => (item.percentage = true),
                                                },
                                                {
                                                    icon: 'mdi-cash',
                                                    active: !item.percentage,
                                                    onClick: () => (item.percentage = false),
                                                },
                                            ]"
                                            :append-text="!item.percentage ? tariffCurrency : null"
                                        />
                                    </template>
                                    <template v-slot:item.actions="{item}">
                                        <div class="full-width d-flex align-center">
                                            <v-spacer />
                                            <v-btn
                                                size="small"
                                                color="error"
                                                variant="text"
                                                icon="mdi-close"
                                                @click="removeAccommodationSurcharge(item)"
                                            />
                                        </div>
                                    </template>
                                </v-data-table>
                                <v-divider class="mb-4" />
                                <v-row dense>
                                    <v-col cols="12">
                                        <v-btn
                                            variant="flat"
                                            size="small"
                                            color="primary"
                                            @click="addAccommodationSurcharge"
                                        >
                                            {{ $t('add') }}
                                        </v-btn>
                                    </v-col>
                                </v-row>
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>
                -->

                <!-- Length of Stay Surcharges block hidden pending B2C application audit.
                <v-row dense>
                    <v-col cols="12">
                        <v-card variant="outlined" :loading="isServicesLoading">
                            <v-card-title>
                                <span class="font-weight-bold text-subtitle-2">
                                    {{ $t('length_of_stay_surcharges') }}
                                </span>
                            </v-card-title>
                            <v-card-text>
                                <v-data-table
                                    :items="lengthOfStayVisible"
                                    :headers="headersDuration"
                                    hide-default-footer
                                    :items-per-page="-1"
                                >
                                    <template v-slot:item.service="{item}">
                                        <div class="my-3">
                                            <v-select
                                                v-model="item.serviceId"
                                                :items="selectableServices"
                                                item-value="id"
                                                :item-title="serviceTitle"
                                                :rules="[requiredRule('service')]"
                                                hide-details
                                                density="compact"
                                            />
                                        </div>
                                    </template>
                                    <template v-slot:item.minNights="{item}">
                                        <v-number-input
                                            v-model="item.minNights"
                                            :min="0"
                                            hide-details
                                            density="compact"
                                        />
                                    </template>
                                    <template v-slot:item.maxNights="{item}">
                                        <v-number-input
                                            v-model="item.maxNights"
                                            :min="0"
                                            hide-details
                                            density="compact"
                                        />
                                    </template>
                                    <template v-slot:item.amount="{item}">
                                        <NumberInputWithButtons
                                            v-model="item.amount"
                                            :label="item.percentage ? `${$t('percent')}*` : `${$t('amount')}*`"
                                            :width="200"
                                            hide-details
                                            :rules="[requiredRule('')]"
                                            :buttons="[
                                                {
                                                    icon: 'mdi-percent',
                                                    active: item.percentage,
                                                    onClick: () => (item.percentage = true),
                                                },
                                                {
                                                    icon: 'mdi-cash',
                                                    active: !item.percentage,
                                                    onClick: () => (item.percentage = false),
                                                },
                                            ]"
                                            :append-text="!item.percentage ? tariffCurrency : null"
                                        />
                                    </template>
                                    <template v-slot:item.actions="{item}">
                                        <div class="full-width d-flex align-center">
                                            <v-spacer />
                                            <v-btn
                                                size="small"
                                                color="error"
                                                variant="text"
                                                icon="mdi-close"
                                                @click="removeLengthOfStaySurcharge(item)"
                                            />
                                        </div>
                                    </template>
                                </v-data-table>
                                <v-divider class="mb-4" />
                                <v-row dense>
                                    <v-col cols="12">
                                        <v-btn
                                            variant="flat"
                                            size="small"
                                            color="primary"
                                            @click="addLengthOfStaySurcharge"
                                        >
                                            {{ $t('add') }}
                                        </v-btn>
                                    </v-col>
                                </v-row>
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>
                -->

                <!-- Description block hidden.
                <v-row dense>
                    <v-col cols="12">
                        <v-card variant="outlined">
                            <v-card-title>
                                <span class="font-weight-bold text-subtitle-2">
                                    {{ $t('description') }}
                                </span>
                            </v-card-title>
                            <v-card-text>
                                <v-textarea
                                    v-model="surchargeForEdit.description"
                                    :placeholder="`${$t('description')}`"
                                    rows="3"
                                />
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>
                -->
            </v-form>
        </v-card-text>
        <v-card-actions class="d-flex justify-start px-6 pb-6 pt-4">
            <v-btn color="primary" :loading="isSaveLoading" data-cy="save" variant="elevated" @click="save">
                {{ $t('save') }}
            </v-btn>
            <v-btn data-cy="close" variant="text" @click="onClose">
                {{ $t('cancel') }}
            </v-btn>
        </v-card-actions>
    </v-card>
</template>

<script lang="ts">
    import {Component, Emit, mixins, Prop} from 'vue-facing-decorator'
    import {FormValidationRulesMixin} from '@/mixins/FormValidationRulesMixin.vue'
    import {clone} from '@/utils/helpers'
    import DatePicker from '@/components/snippets/forms/DatePicker.vue'
    import {supplierStore} from '~src/composables/store-accessor'
    import {contractServiceType} from '~/utils/types/types'

    @Component({
        components: {DatePicker},
    })
    export class ProductPricesSurchargesModal extends mixins(FormValidationRulesMixin) {
        @Prop() surcharge
        @Prop() contract
        @Prop() selectedLocale

        isValidGeneral = false

        surchargeForEdit = null

        activeTariffs = []
        inactiveTariffs = []
        isTariffsLoading = false

        services = []
        isServicesLoading = false

        hotelServices = []
        roomTypes = []

        mealTypes = []
        isMealTypesLoading = false
        paxes = []
        adultPax = null
        mealPrices = []
        extraPlacePrices = []
        withoutPlacePrices = []

        isSaveLoading = false
        editMode = false

        get isOwnHotel() {
            return this.contract?.serviceType === contractServiceType.ownHotel
        }

        async mounted() {
            await this.initValues()
        }

        async initValues() {
            if (this.surcharge) {
                this.surchargeForEdit = clone(this.surcharge)
            } else {
                this.surchargeForEdit = {
                    contractId: this.contract.id,
                    name: '',
                    description: '',
                    active: true,
                    tariffLinks: [],
                    prices: [],
                    accommodationSurcharges: [],
                    lengthOfStaySurcharges: [],
                }
            }
            this.surchargeForEdit.tariffLinks = this.surchargeForEdit.tariffLinks || []
            this.surchargeForEdit.prices = this.surchargeForEdit.prices || []
            this.surchargeForEdit.accommodationSurcharges = this.surchargeForEdit.accommodationSurcharges || []
            this.surchargeForEdit.lengthOfStaySurcharges = this.surchargeForEdit.lengthOfStaySurcharges || []

            this.editMode = !!this.surchargeForEdit.id

            await Promise.all([
                this.loadTariffs(),
                this.loadServices(),
                ...(this.isOwnHotel
                    ? [this.loadHotelServices(), this.loadRoomTypes(), this.loadMealTypes(), this.loadPaxes()]
                    : []),
            ])

            if (!this.editMode && !this.surchargeForEdit.tariffLinks.length && this.activeTariffs.length) {
                this.addTariffLink()
            }

            this.seedMealPrices()
            this.seedPlacePrices('Extra')
            this.seedPlacePrices('WithoutPlace')
        }

        clear() {
            this.surchargeForEdit = null
            this.activeTariffs = []
            this.inactiveTariffs = []
            this.services = []
            this.hotelServices = []
            this.roomTypes = []
            this.mealTypes = []
            this.paxes = []
            this.adultPax = null
            this.mealPrices = []
            this.extraPlacePrices = []
            this.withoutPlacePrices = []
        }

        async loadTariffs() {
            try {
                this.isTariffsLoading = true
                const [activeTariffs, inactiveTariffs] = await Promise.all([
                    this.$api.tariffs.get({contractId: this.contract.id, active: true}),
                    this.$api.tariffs.get({contractId: this.contract.id, active: false}),
                ])
                this.activeTariffs = activeTariffs.tariffs
                this.inactiveTariffs = inactiveTariffs.tariffs
            } catch (e) {
                this.$toast.error(this.$t('error_message.load_error'))
            } finally {
                this.isTariffsLoading = false
            }
        }

        async loadServices() {
            try {
                this.isServicesLoading = true
                const response = await this.$api.ownProductServices.get({contractId: this.contract.id})
                this.services = (response.services || []).filter(({active}) => active)
            } catch (e) {
                this.$toast.error(this.$t('error_message.load_error'))
            } finally {
                this.isServicesLoading = false
            }
        }

        async loadHotelServices() {
            try {
                const response = await this.$api.ownHotelServices.get({contractId: this.contract.id})
                this.hotelServices = response.services || []
            } catch (e) {
                this.hotelServices = []
            }
        }

        async loadRoomTypes() {
            try {
                const hotelId = supplierStore.hotelInfo?.hotelId
                if (!hotelId) {
                    this.roomTypes = []
                    return
                }
                const {hotelRoomTypes} = await this.$api.hotelDescriptionRoomType.get(hotelId, {locale: 'all'})
                this.roomTypes = (hotelRoomTypes || []).map(rt => ({
                    ...rt,
                    sustainabilityLevel: rt.sustainabilityLevel || 'BASIC',
                }))
            } catch (e) {
                this.roomTypes = []
            }
        }

        async loadMealTypes() {
            try {
                this.isMealTypesLoading = true
                const response = await this.$api.ownHotelSecondaryServices.get({contractId: this.contract.id})
                this.mealTypes = (response.services || []).filter(({active}) => active)
            } catch (e) {
                this.mealTypes = []
            } finally {
                this.isMealTypesLoading = false
            }
        }

        async loadPaxes() {
            try {
                const response = await this.$api.ownProductPaxes.get({contractId: this.contract.id})
                this.paxes = response.paxes || []
                this.adultPax = this.paxes.find(p => p.isDefault) || this.paxes[0] || null
            } catch (e) {
                this.paxes = []
                this.adultPax = null
            }
        }

        seedMealPrices() {
            if (!this.isOwnHotel || !this.paxes.length || !this.mealTypes.length) {
                this.mealPrices = []
                return
            }
            const existing = this.surchargeForEdit.prices || []
            const rows = []
            this.mealTypes.forEach(mt => {
                this.paxes.forEach(pax => {
                    const found = existing.find(
                        p =>
                            p.secondaryServiceId === mt.id &&
                            p.paxId === pax.id &&
                            (p.serviceId === null || p.serviceId === undefined)
                    )
                    rows.push(
                        found || {
                            secondaryServiceId: mt.id,
                            paxId: pax.id,
                            amount: null,
                            percentage: false,
                            placeType: 'Base',
                        }
                    )
                })
            })
            this.mealPrices = rows
        }

        getMealPrice(secondaryServiceId, paxId) {
            return this.mealPrices.find(p => p.secondaryServiceId === secondaryServiceId && p.paxId === paxId)
        }

        getServiceRoomType(serviceId) {
            const hotelService = this.hotelServices.find(s => s.id === serviceId)
            if (hotelService?.hotelRoomTypeId == null) return null
            return this.roomTypes.find(r => r.id === hotelService.hotelRoomTypeId) || null
        }

        get extraPlaceRooms() {
            return (this.services || []).filter(s => {
                const rt = this.getServiceRoomType(s.id)
                return (rt?.maxExtraPlace != null && rt.maxExtraPlace > 0) || (s.maxExtraplace != null && s.maxExtraplace > 0)
            })
        }

        get withoutPlaceRooms() {
            return (this.services || []).filter(s => {
                const rt = this.getServiceRoomType(s.id)
                return (rt?.maxWithoutPlace != null && rt.maxWithoutPlace > 0) || (s.maxWithoutPlace != null && s.maxWithoutPlace > 0)
            })
        }

        seedPlacePrices(placeType) {
            const target = placeType === 'Extra' ? 'extraPlacePrices' : 'withoutPlacePrices'
            const rooms = placeType === 'Extra' ? this.extraPlaceRooms : this.withoutPlaceRooms
            if (!this.isOwnHotel || !this.paxes.length || !rooms.length) {
                this[target] = []
                return
            }
            const existing = this.surchargeForEdit.prices || []
            const rows = []
            rooms.forEach(svc => {
                this.paxes.forEach(pax => {
                    const found = existing.find(
                        p =>
                            p.serviceId === svc.id &&
                            p.paxId === pax.id &&
                            p.placeType === placeType &&
                            (p.secondaryServiceId === null || p.secondaryServiceId === undefined)
                    )
                    rows.push(
                        found || {
                            serviceId: svc.id,
                            paxId: pax.id,
                            amount: null,
                            percentage: false,
                            placeType,
                        }
                    )
                })
            })
            this[target] = rows
        }

        getExtraPlacePrice(serviceId, paxId) {
            return this.extraPlacePrices.find(p => p.serviceId === serviceId && p.paxId === paxId)
        }

        getWithoutPlacePrice(serviceId, paxId) {
            return this.withoutPlacePrices.find(p => p.serviceId === serviceId && p.paxId === paxId)
        }

        mealTypeName(secondaryServiceId) {
            const mt = this.mealTypes.find(s => s.id === secondaryServiceId)
            if (!mt) return ''
            if (mt.mealTypeName) return mt.mealTypeName
            if (mt.name && typeof mt.name === 'object') {
                return mt.name[this.selectedLocale] || mt.name.en || Object.values(mt.name)[0] || ''
            }
            return mt.name || ''
        }

        applyMealPricesToPayload() {
            if (!this.isOwnHotel) return
            const isMealRow = p =>
                p.paxId != null && p.secondaryServiceId != null && (p.serviceId === null || p.serviceId === undefined)
            const isPlaceRow = (p, placeType) =>
                p.paxId != null &&
                p.serviceId != null &&
                p.placeType === placeType &&
                (p.secondaryServiceId === null || p.secondaryServiceId === undefined)
            const isFilled = p => p.amount !== null && p.amount !== '' && p.amount !== undefined
            const other = (this.surchargeForEdit.prices || []).filter(
                p => !isMealRow(p) && !isPlaceRow(p, 'Extra') && !isPlaceRow(p, 'WithoutPlace')
            )
            this.surchargeForEdit.prices = [
                ...other,
                ...this.mealPrices.filter(isFilled),
                ...this.extraPlacePrices.filter(isFilled),
                ...this.withoutPlacePrices.filter(isFilled),
            ]
        }

        getSustainabilityLevel(serviceId) {
            if (!this.isOwnHotel) return null
            const hotelService = this.hotelServices.find(s => s.id === serviceId)
            const roomTypeId = hotelService?.hotelRoomTypeId
            if (roomTypeId != null) {
                const rt = this.roomTypes.find(r => r.id === roomTypeId)
                if (rt) return rt.sustainabilityLevel || 'BASIC'
            }
            const direct = this.roomTypes.find(r => r.id === serviceId)
            return direct ? direct.sustainabilityLevel || 'BASIC' : null
        }

        get selectableServices() {
            if (!this.isOwnHotel) return this.services
            if (!this.roomTypes.length || !this.hotelServices.length) return this.services
            return this.services.filter(svc => this.getSustainabilityLevel(svc.id) === 'BASIC')
        }

        serviceTitle = service => {
            if (!service) return ''
            let base
            if (service.name && typeof service.name === 'object') {
                base = service.name[this.selectedLocale] || Object.values(service.name)[0] || ''
            } else {
                base = service.name || service.code || ''
            }
            const level = this.getSustainabilityLevel(service.id)
            return level && level !== 'BASIC' ? `${base} (${level})` : base
        }

        @Emit()
        onClose() {
            this.clear()
        }

        @Emit()
        onSave() {}

        async save() {
            await this.$refs.form.validate()
            if (!this.isValidGeneral) return

            this.applyMealPricesToPayload()

            if (this.editMode) {
                await this.saveEdit()
            } else {
                await this.saveCreate()
            }
        }

        async saveEdit() {
            try {
                this.isSaveLoading = true
                await this.$api.ownProductPriceListSurcharges.put(this.surchargeForEdit)
                this.$toast.success(this.$t('success_message.saved_successfully'))
                this.onClose()
                this.onSave()
            } catch (e) {
                this.$toast.error(e.msg || e.errors?.[0]?.message || this.$t('error_message.save_error'))
            } finally {
                this.isSaveLoading = false
            }
        }

        async saveCreate() {
            try {
                this.isSaveLoading = true
                await this.$api.ownProductPriceListSurcharges.post(this.surchargeForEdit)
                this.$toast.success(this.$t('success_message.saved_successfully'))
                this.onClose()
                this.onSave()
            } catch (e) {
                this.$toast.error(e.msg || e.errors?.[0]?.message || this.$t('error_message.save_error'))
            } finally {
                this.isSaveLoading = false
            }
        }

        addTariffLink() {
            const first = this.activeTariffs[0]
            this.surchargeForEdit.tariffLinks.push({
                tariffId: first ? first.id : null,
                dateFrom: first ? first.dateFrom : null,
                dateTo: first ? first.dateTo : null,
                priority: this.surchargeForEdit.tariffLinks.length + 1,
            })
        }

        removeTariffLink(index) {
            this.surchargeForEdit.tariffLinks.splice(index, 1)
        }

        copyTariffLink(tariffLink) {
            this.surchargeForEdit.tariffLinks.push({
                tariffId: tariffLink.tariffId,
                dateFrom: tariffLink.dateFrom,
                dateTo: tariffLink.dateTo,
                priority: this.surchargeForEdit.tariffLinks.length + 1,
            })
        }

        // Accommodation Surcharges UI disabled pending B2C application audit.
        // Existing accommodationSurcharges from the backend are preserved round-trip
        // (loaded into surchargeForEdit, sent back unchanged on save).
        /*
        addAccommodationSurcharge() {
            const first = this.selectableServices[0]
            this.surchargeForEdit.accommodationSurcharges.push({
                serviceId: first ? first.id : null,
                minGuests: null,
                maxGuests: null,
                amount: null,
                percentage: true,
            })
        }

        removeAccommodationSurcharge(item) {
            if (item.id) {
                item.delete = true
            } else {
                const idx = this.surchargeForEdit.accommodationSurcharges.indexOf(item)
                if (idx >= 0) this.surchargeForEdit.accommodationSurcharges.splice(idx, 1)
            }
        }
        */

        // Length of Stay Surcharges UI disabled pending B2C application audit.
        // Existing lengthOfStaySurcharges from the backend are preserved round-trip.
        /*
        addLengthOfStaySurcharge() {
            const first = this.selectableServices[0]
            this.surchargeForEdit.lengthOfStaySurcharges.push({
                serviceId: first ? first.id : null,
                minNights: null,
                maxNights: null,
                amount: null,
                percentage: true,
            })
        }

        removeLengthOfStaySurcharge(item) {
            if (item.id) {
                item.delete = true
            } else {
                const idx = this.surchargeForEdit.lengthOfStaySurcharges.indexOf(item)
                if (idx >= 0) this.surchargeForEdit.lengthOfStaySurcharges.splice(idx, 1)
            }
        }
        */

        /*
        get accommodationVisible() {
            return (this.surchargeForEdit?.accommodationSurcharges || []).filter(s => !s.delete)
        }
        */

        /*
        get lengthOfStayVisible() {
            return (this.surchargeForEdit?.lengthOfStaySurcharges || []).filter(s => !s.delete)
        }
        */

        get headersTariffs() {
            return [
                {title: this.$t('tariff'), value: 'tariff', minWidth: '150px'},
                {title: this.$t('status'), value: 'active'},
                {title: this.$t('dates'), value: 'dates', minWidth: '400px'},
                {title: this.$t('actions'), value: 'actions', align: 'end'},
            ]
        }

        get tariffs() {
            return [...this.activeTariffs, ...this.inactiveTariffs]
        }

        tariffItems(id) {
            return !id || this.tariffStatus(id) ? this.activeTariffs : this.inactiveTariffs
        }

        tariffStatus(id) {
            return !!this.tariffs.find(tariff => tariff.id === id)?.active
        }

        get paxColumns() {
            return this.paxes.map(pax => {
                const base =
                    pax.name && typeof pax.name === 'object'
                        ? pax.name[this.selectedLocale] || pax.name.en || Object.values(pax.name)[0] || `Pax ${pax.id}`
                        : pax.name || `Pax ${pax.id}`
                return {
                    title: `${base}${pax.isDefault ? '*' : ''}`,
                    value: `pax_${pax.id}`,
                    minWidth: '200px',
                    align: 'start',
                }
            })
        }

        get headersMealTypes() {
            return [{title: this.$t('meal_type'), value: 'mealType', minWidth: '200px'}, ...this.paxColumns]
        }

        get headersExtraPlaces() {
            return [{title: this.$t('room_type'), value: 'roomType', minWidth: '200px'}, ...this.paxColumns]
        }

        get headersWithoutPlaces() {
            return [{title: this.$t('room_type'), value: 'roomType', minWidth: '200px'}, ...this.paxColumns]
        }

        /*
        get headersAllocation() {
            return [
                {title: this.$t('room_type'), value: 'service', minWidth: '200px'},
                {title: this.$t('min_guests'), value: 'minGuests', width: '120px'},
                {title: this.$t('max_guests'), value: 'maxGuests', width: '120px'},
                {title: this.$t('price'), value: 'amount', minWidth: '220px'},
                {title: this.$t('actions'), value: 'actions', align: 'end'},
            ]
        }
        */

        /*
        get headersDuration() {
            return [
                {title: this.$t('room_type'), value: 'service', minWidth: '200px'},
                {title: this.$t('min_nights'), value: 'minNights', width: '120px'},
                {title: this.$t('max_nights'), value: 'maxNights', width: '120px'},
                {title: this.$t('price'), value: 'amount', minWidth: '220px'},
                {title: this.$t('actions'), value: 'actions', align: 'end'},
            ]
        }
        */

        get tariffCurrency() {
            if (!this.tariffs.length || !this.surchargeForEdit?.tariffLinks?.length) return ''
            return this.tariffs.find(({id}) => id === this.surchargeForEdit.tariffLinks[0].tariffId)?.currencyCode
        }
    }

    export default ProductPricesSurchargesModal
</script>
